import { useMemo } from 'react';
import moment from 'moment';
import { yesterday } from '../AccountPerformanceReporting.constants';
import { useInceptionDate } from './useInceptionDate';
import { PerformancePeriodDTO } from '../types';
import { useOptions } from './useOptions';

export interface RangePreset {
  label: string;
  value: PerformancePeriodDTO;
  dates: string[];
}

export const useRangePresets = (): RangePreset[] => {
  const { performancePeriodOptions } = useOptions();

  const inceptionDate = useInceptionDate();

  return useMemo(() => {
    return performancePeriodOptions.reduce((acc, { label, value, range }) => {
      const { startDate, endDate } = range;

      /**
       * The Performance Period options come from the BE having the oldest
       * inception date of all the accounts as the start date in the since
       * inception period. However, individual accounts may have different
       * inception dates. That is why we need to tweak this period to contain
       * the correct inception date.
       *
       * Also, we should filter out this period for groups/accounts without inception dates.
       */
      if (value === PerformancePeriodDTO.AllTime) {
        if (inceptionDate) {
          acc.push({
            label,
            value,
            dates: [inceptionDate, yesterday],
          });
        }

        return acc;
      }

      // Period is not valid
      if (inceptionDate && moment(inceptionDate).isAfter(startDate)) {
        return acc;
      }

      acc.push({
        label,
        value,
        dates: [startDate, endDate],
      });

      return acc;
    }, [] as RangePreset[]);
  }, [inceptionDate, performancePeriodOptions]);
};
